
import React, { useState } from 'react';
import { MaintenanceItem, Status } from '../../types';
import MaintenanceCard from './MaintenanceCard';

/**
 * Props for the MaintenanceKanbanBoard component.
 */
interface MaintenanceKanbanBoardProps {
  items: MaintenanceItem[];
  onViewDetails: (itemId: number) => void;
  onMarkAsCompleted: (itemId: number) => void;
  onItemStatusChange: (itemId: number, newStatus: Status) => void;
}

// Defines the order of the columns on the board.
const statusOrder: Status[] = [
  Status.Open,
  Status.InProgress,
  Status.Pending,
  Status.Completed,
];

// Style mappings for each status column to give them distinct visual cues.
const statusStyles: { [key in Status]: { border: string; text: string; bg: string } } = {
    [Status.Open]: { border: 'border-red-500', text: 'text-red-800', bg: 'bg-red-50' },
    [Status.InProgress]: { border: 'border-yellow-500', text: 'text-yellow-800', bg: 'bg-yellow-50' },
    [Status.Pending]: { border: 'border-blue-500', text: 'text-blue-800', bg: 'bg-blue-50' },
    [Status.Completed]: { border: 'border-green-500', text: 'text-green-800', bg: 'bg-green-50' },
};

/**
 * A component that renders a Kanban-style board, organizing maintenance items into columns by status.
 * Supports drag-and-drop to change an item's status.
 * @param {MaintenanceKanbanBoardProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered Kanban board.
 */
const MaintenanceKanbanBoard: React.FC<MaintenanceKanbanBoardProps> = ({ items, onViewDetails, onMarkAsCompleted, onItemStatusChange }) => {
  const [draggedItemId, setDraggedItemId] = useState<number | null>(null);
  const [draggedOverColumn, setDraggedOverColumn] = useState<Status | null>(null);

  /**
   * Memoized grouping of items by their status.
   */
  const groupedItems = React.useMemo(() => {
    return items.reduce((acc, item) => {
      const status = item.status as Status;
      if (!acc[status]) acc[status] = [];
      acc[status].push(item);
      return acc;
    }, {} as Record<Status, MaintenanceItem[]>);
  }, [items]);

  const handleDragStart = (e: React.DragEvent, item: MaintenanceItem) => {
    if (item.isOptimistic || item.status === Status.Completed) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('itemId', item.id.toString());
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => setDraggedItemId(item.id), 0);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDraggedOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent, status: Status) => {
    e.preventDefault();
    const itemBeingDragged = items.find(i => i.id === draggedItemId);
    if (itemBeingDragged && itemBeingDragged.status !== status) {
      setDraggedOverColumn(status);
    }
  };

  const handleDragLeave = () => {
    setDraggedOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, newStatus: Status) => {
    e.preventDefault();
    const itemIdStr = e.dataTransfer.getData('itemId');
    if (!itemIdStr) return;
    
    const itemId = parseInt(itemIdStr, 10);
    const itemBeingDragged = items.find(i => i.id === itemId);

    if (itemBeingDragged && itemBeingDragged.status !== newStatus) {
      onItemStatusChange(itemId, newStatus);
    }
    
    setDraggedOverColumn(null);
  };

  return (
    <div
      className="flex gap-4 overflow-x-auto p-2 h-[calc(100vh-14rem)] w-full"
      onDragEnd={handleDragEnd}
    >
      {statusOrder.map(status => {
        const columnItems = groupedItems[status] || [];
        const styles = statusStyles[status];
        const isDraggedOver = draggedOverColumn === status;

        return (
          <div
            key={status}
            className="flex-1 min-w-[280px] h-full flex flex-col"
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, status)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, status)}
          >
            <div className="flex flex-col h-full bg-gray-100 rounded-xl shadow-sm overflow-hidden">
              <div className={`p-4 border-b-4 ${styles.border} ${styles.bg} flex-shrink-0`}>
                <div className="flex justify-between items-center">
                  <h3 className={`font-bold text-lg ${styles.text} capitalize`}>{status}</h3>
                  <span className={`text-sm font-semibold px-2.5 py-1 rounded-full ${styles.bg} ${styles.text}`}>{columnItems.length}</span>
                </div>
              </div>
              <div className={`p-4 space-y-4 overflow-y-auto flex-grow transition-colors duration-300 ease-in-out ${isDraggedOver ? 'bg-blue-100 ring-2 ring-blue-400 ring-inset' : ''}`}>
                {columnItems.length > 0 ? (
                  columnItems.map(item => (
                    <MaintenanceCard
                      key={item.id}
                      item={item}
                      onViewDetails={() => onViewDetails(item.id)}
                      onMarkAsCompleted={onMarkAsCompleted}
                      onDragStart={(e) => handleDragStart(e, item)}
                      isDragging={draggedItemId === item.id}
                    />
                  ))
                ) : (
                  <div className={`text-center text-gray-400 py-10 px-4 text-sm rounded-lg border-2 border-dashed border-gray-300 ${isDraggedOver ? 'border-blue-400' : ''}`}>
                    Drop tasks here
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MaintenanceKanbanBoard;
